import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ImagePlus, X } from "lucide-react";

export default function CreatePost() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user === false)) {
      nav("/login");
    }
  }, [user, loading, nav]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 6 - images.length;
    if (room <= 0) { toast.error("Maximum 6 images"); return; }
    const toUpload = files.slice(0, room);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is too large (max 5MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setImages([...images, ...uploaded]);
      if (files.length > room) toast.info(`Only added ${room} image${room === 1 ? "" : "s"} — 6 max`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setImages(images.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Please pick a category");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/posts", {
        title,
        body,
        category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        image_url: imageUrl,
      });
      toast.success("Post published!");
      nav(`/p/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#857163] mb-1.5">New post</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">
            Share something <span className="text-[#FF7E67]">sweet</span>
          </h1>
        </div>

        <form onSubmit={submit} className="cp-card p-6 sm:p-8 space-y-5" data-testid="create-post-form">
          <div>
            <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Title</label>
            <input
              className="cp-input"
              placeholder="e.g. My no-crack chocolate cake pop trick"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength={3}
              maxLength={200}
              data-testid="post-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Category</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategory(c.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
                    category === c.slug
                      ? "bg-[#FF7E67] text-white border-[#FF7E67]"
                      : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                  }`}
                  data-testid={`category-btn-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Body</label>
            <textarea
              className="cp-input min-h-[180px] resize-y"
              placeholder="Share your recipe, tip, question, or story…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              data-testid="post-body-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Tags (comma-separated)</label>
            <input
              className="cp-input"
              placeholder="chocolate, birthday, no-crack"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              data-testid="post-tags-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Images (up to 6, optional)</label>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="image-thumbs">
                {images.map((path, i) => (
                  <div key={path} className="relative aspect-square">
                    <img src={fileUrl(path)} alt="" className="w-full h-full object-cover rounded-2xl border border-[#EBDCD1]" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur rounded-full p-1 shadow hover:scale-105 transition"
                      data-testid={`remove-image-${i}`}
                    >
                      <X className="w-3.5 h-3.5 text-[#3B2C24]" />
                    </button>
                    {i === 0 && <span className="absolute bottom-1.5 left-1.5 bg-white/90 rounded-full px-2 py-0.5 text-[10px] font-semibold text-[#3B2C24]">Cover</span>}
                  </div>
                ))}
              </div>
            )}
            {images.length < 6 && (
              <label className="cp-card p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[#F9EFE6]/50 transition" data-testid="upload-image-label">
                <input type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="upload-image-input" />
                <ImagePlus className="w-8 h-8 text-[#FF7E67] mb-2" />
                <span className="text-sm text-[#857163]">
                  {uploading ? "Uploading…" : `Click to add ${images.length ? "more photos" : "photos"} (${6 - images.length} left, max 5MB each)`}
                </span>
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#EBDCD1]">
            <button type="button" onClick={() => nav("/")} className="cp-btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="cp-btn-primary disabled:opacity-70" data-testid="publish-post-btn">
              {submitting ? "Publishing…" : "Publish post"}
            </button>
          </div>
        </form>
      </main>
    </>
  );
}
